import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { SiteNav } from '@/lib/content'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/generals/page-header'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/primitives'
import { contact, siteConfig } from '@/constants/site'
import { getSiteNav } from '@/lib/content'

interface PrivacyPageProps {
    nav: SiteNav
}

const PrivacyPage: FC<PrivacyPageProps> = ({
    nav = { services: [], areas: [] }
}): ReactNode => {
    return (
        <Layout nav={nav}>
            <Seo
                title='Privacy notice'
                description='How Capital Craft Construction Ltd collects, uses and stores the personal information you send through this website.'
                path='/privacy'
            />

            <PageHeader
                eyebrow='Legal'
                title='Privacy notice'
                lead='What we collect when you contact us, why we hold it, and how to ask us to delete it.'
                crumbs={[
                    { name: 'Home', href: '/' },
                    { name: 'Privacy notice', href: '/privacy' }
                ]}
            />

            <section className='bg-paper py-16 lg:py-24'>
                <Container>
                    <Reveal>
                        <div className='prose-craft max-w-[68ch] text-[1.02rem] text-ink'>
                            <h2>Who we are</h2>

                            <p>
                                {siteConfig.legalName} is the data controller for the information you
                                send through this website. You can reach us at {contact.email} or on{' '}
                                {contact.phone}.
                            </p>

                            <h2>What we collect</h2>

                            <p>
                                When you complete the enquiry form we collect your name, email
                                address, the property postcode and the description of the work you
                                are planning. Your phone number, budget range and preferred start
                                date are optional. We also record which page the enquiry came from,
                                so we know what you were reading when you got in touch.
                            </p>

                            <h2>Why we hold it</h2>

                            <p>
                                We use it for one purpose: to answer your enquiry, arrange a site
                                visit and prepare a quotation. That is a legitimate interest under
                                the UK GDPR, and it is the reason you contacted us. We do not sell
                                your details, we do not pass them to other trades without asking you
                                first, and we do not add you to a marketing list.
                            </p>

                            <h2>How long we keep it</h2>

                            <p>
                                Enquiries that do not turn into work are deleted after twelve months.
                                Where we carry out work for you, we keep the project records for six
                                years, which is the period we may need them for insurance, warranty
                                and tax purposes.
                            </p>

                            <h2>Who else sees it</h2>

                            <p>
                                Your enquiry is stored on our website hosting and database
                                infrastructure and read by the people at Capital Craft who handle
                                quotations. Where a structural engineer or Building Control body
                                needs your address to carry out the work, we share only what that
                                requires.
                            </p>

                            <h2>Your rights</h2>

                            <p>
                                You can ask us for a copy of what we hold, ask us to correct it, or
                                ask us to delete it. Email {contact.email} and we will respond within
                                one month. If you are not satisfied with how we have handled your
                                information, you can complain to the Information Commissioners
                                Office at ico.org.uk.
                            </p>

                            <h2>Cookies</h2>

                            <p>
                                This site does not set advertising or tracking cookies, and it does
                                not profile you across other websites. Visitor numbers are counted in
                                aggregate, without cookies and without identifying individuals.
                            </p>
                        </div>
                    </Reveal>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<PrivacyPageProps> = async () => {
    const nav = await getSiteNav()

    return {
        props: { nav },
        revalidate: 3600
    }
}

export default PrivacyPage
